import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, X, AlertTriangle } from "lucide-react";
import api from "../lib/api";

// Admin-only in-app alerts: surfaces failed AI lookups + system/sync failures.
export default function SystemAlerts() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [failed, setFailed] = useState(0);
  const [alerts, setAlerts] = useState([]);

  const poll = useCallback(() => {
    api.get("/notifications").then((r) => {
      setCount(r.data.system_alerts || 0);
      setFailed(r.data.failed_enrichments || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    poll();
    const id = setInterval(poll, 30000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [poll]);

  const openPanel = async () => {
    setOpen(true);
    try {
      const { data } = await api.get("/system-alerts");
      setAlerts(data.alerts || []);
      setFailed(data.failed_enrichments || 0);
    } catch { /* ignore */ }
  };

  const dismissAll = async () => {
    await api.post("/system-alerts/ack").catch(() => {});
    setAlerts([]);
    setCount(0);
  };

  const total = count + (failed > 0 ? 1 : 0);

  return (
    <div className="relative">
      <button
        onClick={() => (open ? setOpen(false) : openPanel())}
        data-testid="system-alerts-button"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-stone-600 hover:bg-stone-200/60 transition-colors"
      >
        <Bell className="h-4 w-4" />
        <span className="flex-1 text-left">System alerts</span>
        {total > 0 && (
          <span data-testid="system-alerts-badge"
            className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-semibold flex items-center justify-center">
            {total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute bottom-12 left-0 w-72 max-h-96 overflow-y-auto bg-white border border-stone-200 rounded-lg shadow-xl z-50 p-3"
          data-testid="system-alerts-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-stone-900">System alerts</p>
            <button onClick={() => setOpen(false)} data-testid="system-alerts-close">
              <X className="h-4 w-4 text-stone-400" />
            </button>
          </div>

          {failed > 0 && (
            <Link to="/research" onClick={() => setOpen(false)} data-testid="alerts-failed-enrichment"
              className="flex items-start gap-2 p-2.5 rounded-md bg-amber-50 border border-amber-200 mb-2 hover:bg-amber-100 transition-colors">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-medium text-amber-800">{failed} AI lookup(s) failed</p>
                <p className="text-[11px] text-amber-700">Open Research → Retry failed</p>
              </div>
            </Link>
          )}

          {alerts.length === 0 && failed === 0 && (
            <p className="text-xs text-stone-400 text-center py-6" data-testid="no-alerts">No alerts. All systems healthy.</p>
          )}

          {alerts.map((a) => (
            <div key={a.id}
              className={`p-2.5 rounded-md border mb-1.5 ${a.acknowledged ? "bg-stone-50 border-stone-200 opacity-60" : "bg-red-50 border-red-200"}`}>
              <p className="text-xs font-medium text-stone-800">{a.source}</p>
              <p className="text-[11px] text-stone-600 break-words">{a.message}</p>
              <p className="text-[10px] text-stone-400 mt-0.5">{new Date(a.ts).toLocaleString()}</p>
            </div>
          ))}

          {alerts.some((a) => !a.acknowledged) && (
            <button onClick={dismissAll} data-testid="dismiss-alerts-button"
              className="w-full mt-1 text-xs text-stone-600 hover:text-stone-900 border border-stone-300 rounded-md py-1.5 transition-colors">
              Clear all alerts
            </button>
          )}
        </div>
      )}
    </div>
  );
}
